import React, { useRef, useMemo } from 'react';
import type { TrackInfo } from '../types';
import { Menu } from '../../components/Menu';
import type { MenuItem } from '../../components/Menu';
import { PlayerButton } from './PlayerButton';

interface CaptionsButtonProps {
  tracks: TrackInfo[];
  selectedTrackIdx: number;
  isOpen: boolean;
  onToggle: () => void;
  onSelectTrack: (index: number) => void;
  speechEnabled: boolean;
  speechSupported: boolean;
  onToggleSpeech: () => void;
  onOpenSpeechSettings: () => void;
}

export function CaptionsButton({
  tracks,
  selectedTrackIdx,
  isOpen,
  onToggle,
  onSelectTrack,
  speechEnabled,
  speechSupported,
  onToggleSpeech,
  onOpenSpeechSettings,
}: CaptionsButtonProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const items = useMemo<MenuItem[]>(() => {
    const result: MenuItem[] = [
      {
        type: 'radio',
        id: 'captions-off',
        label: 'Off',
        checked: selectedTrackIdx === -1,
        onClick: () => onSelectTrack(-1),
      },
      ...tracks.map<MenuItem>((track, idx) => ({
        type: 'radio' as const,
        id: `caption-track-${track.srclang}-${idx}`,
        label: track.label,
        checked: selectedTrackIdx === idx,
        onClick: () => onSelectTrack(idx),
      })),
    ];

    if (speechSupported) {
      result.push(
        { type: 'separator', id: 'speech-sep' },
        {
          type: 'checkbox',
          id: 'speak-captions',
          label: 'Speak Captions',
          checked: speechEnabled,
          onClick: onToggleSpeech,
        },
        {
          type: 'action',
          id: 'speech-settings',
          label: 'Speech Settings…',
          onClick: onOpenSpeechSettings,
        },
      );
    }

    return result;
  }, [tracks, selectedTrackIdx, speechSupported, speechEnabled, onSelectTrack, onToggleSpeech, onOpenSpeechSettings]);

  if (tracks.length === 0) return null;

  return (
    <div className="kollab-player-captions-container">
      <PlayerButton
        buttonRef={triggerRef}
        className={`kollab-player-captions-btn ${selectedTrackIdx >= 0 ? 'kollab-player-captions-btn--active' : ''}`}
        tooltip="Captions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={onToggle}
      />

      <Menu
        items={items}
        isOpen={isOpen}
        onClose={onToggle}
        ariaLabel="Caption tracks"
        triggerRef={triggerRef}
        className="kollab-player-captions-dropdown"
      />
    </div>
  );
}
